/**
 * Link utilities for BWS Market Maker website
 * Handles environment-specific external links (local vs production)
 */

import { config, isDevelopment, isProduction } from './config';

export interface ExplorerLinks {
  address: (address: string) => string;
  transaction: (txHash: string) => string;
  block: (blockNumber: number) => string;
  token: (tokenAddress: string) => string;
}

export interface ExternalLinks {
  explorer: ExplorerLinks;
  networkStatus: string;
  gasTracker: string;
  documentation: string;
  github: string;
  support: string;
}

// Get the appropriate blockchain explorer based on environment
const getBlockchainExplorer = (): ExplorerLinks => {
  const chainId = config.blockchain.chainId;
  
  // Local development (Hardhat)
  if (isDevelopment() && chainId === 31337) {
    return {
      address: (address: string) => `http://localhost:8545/address/${address}`,
      transaction: (txHash: string) => `http://localhost:8545/tx/${txHash}`,
      block: (blockNumber: number) => `http://localhost:8545/block/${blockNumber}`,
      token: (tokenAddress: string) => `http://localhost:8545/token/${tokenAddress}`
    };
  }
  
  // Production networks
  switch (chainId) {
    case 1: // Ethereum Mainnet
      return {
        address: (address: string) => `https://etherscan.io/address/${address}`,
        transaction: (txHash: string) => `https://etherscan.io/tx/${txHash}`,
        block: (blockNumber: number) => `https://etherscan.io/block/${blockNumber}`,
        token: (tokenAddress: string) => `https://etherscan.io/token/${tokenAddress}`
      };
    
    case 5: // Goerli Testnet
      return {
        address: (address: string) => `https://goerli.etherscan.io/address/${address}`,
        transaction: (txHash: string) => `https://goerli.etherscan.io/tx/${txHash}`,
        block: (blockNumber: number) => `https://goerli.etherscan.io/block/${blockNumber}`,
        token: (tokenAddress: string) => `https://goerli.etherscan.io/token/${tokenAddress}`
      };
    
    case 11155111: // Sepolia Testnet
      return {
        address: (address: string) => `https://sepolia.etherscan.io/address/${address}`,
        transaction: (txHash: string) => `https://sepolia.etherscan.io/tx/${txHash}`,
        block: (blockNumber: number) => `https://sepolia.etherscan.io/block/${blockNumber}`,
        token: (tokenAddress: string) => `https://sepolia.etherscan.io/token/${tokenAddress}`
      };
    
    case 56: // BSC Mainnet
      return {
        address: (address: string) => `https://bscscan.com/address/${address}`,
        transaction: (txHash: string) => `https://bscscan.com/tx/${txHash}`,
        block: (blockNumber: number) => `https://bscscan.com/block/${blockNumber}`,
        token: (tokenAddress: string) => `https://bscscan.com/token/${tokenAddress}`
      };
    
    case 137: // Polygon Mainnet
      return {
        address: (address: string) => `https://polygonscan.com/address/${address}`,
        transaction: (txHash: string) => `https://polygonscan.com/tx/${txHash}`,
        block: (blockNumber: number) => `https://polygonscan.com/block/${blockNumber}`,
        token: (tokenAddress: string) => `https://polygonscan.com/token/${tokenAddress}`
      };
    
    default:
      // Fallback to Etherscan for unknown networks
      return {
        address: (address: string) => `https://etherscan.io/address/${address}`,
        transaction: (txHash: string) => `https://etherscan.io/tx/${txHash}`,
        block: (blockNumber: number) => `https://etherscan.io/block/${blockNumber}`,
        token: (tokenAddress: string) => `https://etherscan.io/token/${tokenAddress}`
      };
  }
};

// Get network-specific external links
const getNetworkLinks = (): Omit<ExternalLinks, 'explorer'> => {
  const chainId = config.blockchain.chainId;
  
  // Local development
  if (isDevelopment() && chainId === 31337) {
    return {
      networkStatus: 'http://localhost:8545/',
      gasTracker: 'http://localhost:8545/gas',
      documentation: 'https://hardhat.org/hardhat-network/',
      github: 'https://github.com/your-org/bws-token-market-maker',
      support: 'mailto:support@your-domain.com'
    };
  }
  
  // Production networks
  switch (chainId) {
    case 1: // Ethereum Mainnet
      return {
        networkStatus: 'https://ethstats.net/',
        gasTracker: 'https://etherscan.io/gastracker',
        documentation: 'https://docs.your-domain.com',
        github: 'https://github.com/your-org/bws-token-market-maker',
        support: 'https://support.your-domain.com'
      };
    
    default:
      return {
        networkStatus: 'https://ethstats.net/',
        gasTracker: 'https://etherscan.io/gastracker',
        documentation: 'https://docs.your-domain.com',
        github: 'https://github.com/your-org/bws-token-market-maker',
        support: 'https://support.your-domain.com'
      };
  }
};

// Create complete external links object
export const createExternalLinks = (): ExternalLinks => {
  return {
    explorer: getBlockchainExplorer(),
    ...getNetworkLinks()
  };
};

// Global external links instance
export const externalLinks: ExternalLinks = createExternalLinks();

// Convenience functions for common use cases
export const getContractExplorerUrl = (contractType: 'treasury' | 'vault' | 'auditTrail' | 'bwsToken'): string => {
  const address = config.blockchain.contracts[contractType];
  return address ? externalLinks.explorer.address(address) : '#';
};

export const getTransactionExplorerUrl = (txHash: string): string => {
  return externalLinks.explorer.transaction(txHash);
};

export const getBWSTokenExplorerUrl = (): string => {
  return getContractExplorerUrl('bwsToken');
};

// Link validation
export const isValidExternalLink = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return ['http:', 'https:', 'mailto:'].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

// Check if link should open in new tab
export const shouldOpenInNewTab = (url: string): boolean => {
  if (!isValidExternalLink(url)) return false;
  
  try {
    const parsedUrl = new URL(url);
    const currentHost = window.location.hostname;
    
    // Open external links in new tab
    return parsedUrl.hostname !== currentHost && parsedUrl.hostname !== 'localhost';
  } catch {
    return false;
  }
};

// Generate link properties for React components
export const getLinkProps = (url: string) => {
  const isExternal = shouldOpenInNewTab(url);
  
  return {
    href: url,
    target: isExternal ? '_blank' : '_self',
    rel: isExternal ? 'noopener noreferrer' : undefined,
    'data-external': isExternal ? 'true' : 'false'
  };
};

// Environment detection helpers
export const getEnvironmentInfo = () => {
  return {
    environment: config.environment,
    chainId: config.blockchain.chainId,
    isLocalDevelopment: isDevelopment() && config.blockchain.chainId === 31337,
    isProduction: isProduction(),
    explorerName: getExplorerName()
  };
};

export const getExplorerName = (): string => {
  const chainId = config.blockchain.chainId;
  
  if (isDevelopment() && chainId === 31337) {
    return 'Local Blockchain Explorer';
  }
  
  switch (chainId) {
    case 1:
    case 5:
    case 11155111:
      return 'Etherscan';
    case 56:
      return 'BscScan';
    case 137:
      return 'PolygonScan';
    default:
      return 'Blockchain Explorer';
  }
};

// Export default
export default externalLinks;